import { compact, uniq } from 'lodash';

const SEPARATOR = ':';

interface OrgStructureIds {
  companyId: string;
  departmentId?: string;
  teamId?: string;
}

/**
 * Selections in the organisational structure are stored as
 * `company:department:team` paths which can stop at any level, e.g. `company`
 * or `company:department`.
 */
export const buildOrgStructurePath = ({
  companyId,
  departmentId,
  teamId,
}: OrgStructureIds) =>
  compact([companyId, departmentId, teamId]).join(SEPARATOR);

export const parseOrgStructurePath = (path: string): OrgStructureIds => {
  const [companyId = '', departmentId, teamId] = path.split(SEPARATOR);

  return { companyId, departmentId, teamId };
};

export const isOrgStructureDescendant = (path: string, ancestor: string) =>
  path.startsWith(`${ancestor}${SEPARATOR}`);

/**
 * Splits paths into flat lists of ids to be sent to endpoints which accept
 * each organisational structure level as a separate filter. A path only
 * contributes its deepest selected unit, e.g. `company:department:team`
 * becomes just that team id.
 */
export const flattenOrgStructurePaths = (paths: string[] = []) => {
  const companies: string[] = [];
  const departments: string[] = [];
  const teams: string[] = [];

  paths.forEach((path) => {
    const { companyId, departmentId, teamId } = parseOrgStructurePath(path);

    if (teamId) {
      teams.push(teamId);
    } else if (departmentId) {
      departments.push(departmentId);
    } else if (companyId) {
      companies.push(companyId);
    }
  });

  return {
    companies: uniq(companies),
    departments: uniq(departments),
    teams: uniq(teams),
  };
};

/**
 * Merges an organisational-structure selection into the flat company,
 * department and team id lists accepted by list endpoints. Each selected path
 * contributes only its deepest unit.
 */
export const mergeOrgStructureIds = ({
  companies = [],
  departments = [],
  teams = [],
  orgStructure = [],
}: {
  companies?: string[];
  departments?: string[];
  teams?: string[];
  orgStructure?: string[];
}) => {
  const selected = flattenOrgStructurePaths(orgStructure);

  return {
    companies: uniq([...companies, ...selected.companies]),
    departments: uniq([...departments, ...selected.departments]),
    teams: uniq([...teams, ...selected.teams]),
  };
};
